import P_CollapsibleSection from '../common/collapsibleSection/P_CollapsibleSection';
import DataTable from 'react-data-table-component';
import { BuildingOffice2Icon } from '@heroicons/react/24/solid';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { productDataColumns, productTableStyle } from './ProductDataColumns';
import Button from 'src/ui/wards/common/button/Button';
import { productRelatedColumns } from './ProductRelatedNumberColumns';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { useNavigate } from 'react-router-dom';
import { useWaProductStore } from 'src/api/wa/store/WaProductStore';
import useWaProductService from 'src/api/wa/service/WaProductService';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import LinkButton from 'src/ui/wards/common/button/LinkButton';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import InlineAlert from 'src/ui/wards/common/inlineAlert/InlineAlert';
import { FormRow } from 'src/ui/wards/common/form/FormRow';

const commonButtonClassName = `
  w-fit whitespace-nowrap rounded-md border border-gray-400
  bg-gradient-to-b from-gray-100 to-gray-300 px-2 text-gray-800
`;

type PhoneNumberLinksProps = {
  phoneNumber: string | null | undefined;
  isPrefix: boolean;
};

const PhoneNumberLinks: FC<PhoneNumberLinksProps> = ({ phoneNumber, isPrefix }) => {
  if (!phoneNumber) {
    return null;
  }
  const pn = isPrefix ? `710-${phoneNumber}` : phoneNumber;

  return <LinkButton title={pn} phoneNumber={pn} />;
};

const P4_2_SiteDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedWorkplace } = useWaWorkplaceStore();
  const { setSelectedProduct, products, productMasterInfo } = useWaProductStore();
  const { selectedDisaster } = useWaDisasterStore();
  const { fetchProducts, fetchProductMasterInfo } = useWaProductService();

  useEffect(() => {
    if (selectedWorkplace) {
      fetchProducts(selectedWorkplace);
      fetchProductMasterInfo(selectedWorkplace);
    }
  }, [fetchProducts, fetchProductMasterInfo, selectedWorkplace]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 items-center gap-4">
        <div className="flex items-center gap-3">
          <BuildingOffice2Icon className="size-14 shrink-0" />
          <div className="flex flex-col">
            <span className="text-sm text-gray-600">{t('wards.siteDetails.workSite')}</span>
            <span className="text-lg font-bold">{selectedWorkplace?.name}</span>
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button
            title={t('wards.siteDetails.bulkChange')}
            className={commonButtonClassName}
            onClick={() => {
              wardsNavigate(navigate, WaScreensMap[WA_Screen.StatusBulkChange].path);
            }}
          />
          <Button
            title={t('wards.common.back')}
            onClick={() => {
              navigate(-1);
            }}
            className={commonButtonClassName}
          />
        </div>
      </div>
      <InlineAlert />
      <P_CollapsibleSection
        title={t('wards.siteDetails.information')}
        contentClassName="grid grid-flow-row text-left"
      >
        <FormRow title={t('wards.siteDetails.placeOfWork')}>
          <div>{selectedWorkplace?.name}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.branchOfficeName')}>
          <div>{selectedWorkplace?.branchName}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.organizationCode')}>
          <div>{selectedWorkplace?.organizationCode}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.customerAddressPostOffice')}>
          <div>{selectedWorkplace?.postalCode}</div>
        </FormRow>
        <FormRow title={t('wards.common.customerNumber')}>
          <div>{selectedWorkplace?.customerId}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.customerAddress')}>
          <LinkButton
            title={selectedWorkplace?.address as string}
            {...(selectedWorkplace?.address && { googleMapAddress: selectedWorkplace.address })}
          />
        </FormRow>
        <FormRow
          title={`${t('wards.siteDetails.telephoneNumberDay')}: ${t('wards.common.normal')}`}
        >
          <PhoneNumberLinks phoneNumber={selectedWorkplace?.dayPhoneNumber} isPrefix={false} />
        </FormRow>
        <FormRow
          title={`${t('wards.siteDetails.telephoneNumberDay')}: ${t('wards.common.prefix')}`}
        >
          <PhoneNumberLinks phoneNumber={selectedWorkplace?.dayPhoneNumber} isPrefix={true} />
        </FormRow>
        <FormRow
          title={`${t('wards.siteDetails.telephoneNumberNight')}: ${t('wards.common.normal')}`}
        >
          <PhoneNumberLinks phoneNumber={selectedWorkplace?.nightPhoneNumber} isPrefix={false} />
        </FormRow>
        <FormRow
          title={`${t('wards.siteDetails.telephoneNumberNight')}: ${t('wards.common.prefix')}`}
        >
          <PhoneNumberLinks phoneNumber={selectedWorkplace?.nightPhoneNumber} isPrefix={true} />
        </FormRow>
        <FormRow title={t('wards.siteDetails.emergencyClass')}>
          <div>{selectedWorkplace?.emergencyClass}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.remarks')}>
          <div>{selectedWorkplace?.remarks}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.remarkSupplElevator')}>
          <div>{selectedWorkplace?.remarkSupplElevator}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.procedureSupplElevator')}>
          <div>{selectedWorkplace?.procedureSupplElevator}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.remarkSupplHeatpump')}>
          <div>{selectedWorkplace?.remarkSupplHeatpump}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.procedureSupplHeatpump')}>
          <div>{selectedWorkplace?.procedureSupplHeatpump}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.entryProcedure')}>
          <div>{selectedWorkplace?.entryProcedure}</div>
        </FormRow>
      </P_CollapsibleSection>

      <P_CollapsibleSection
        title={t('wards.siteDetails.wideAreaDisasterInfo')}
        contentClassName="grid grid-flow-row text-left"
      >
        <FormRow title={t('wards.siteDetails.disasterId')}>
          <div>{selectedDisaster?.id}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.state')}>
          <div>
            {selectedWorkplace?.details?.statusId
              ? t(getDetailStatusTranslationKey(selectedWorkplace?.details?.statusId))
              : ''}
          </div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.dispatchedWorker')}>
          <div>{selectedWorkplace?.details?.reservations?.[0]?.engineerName}</div>
        </FormRow>
        <FormRow title={t('wards.siteDetails.comment')}>
          <div>{selectedWorkplace?.details?.comment}</div>
        </FormRow>
      </P_CollapsibleSection>
      <P_CollapsibleSection
        title={t('wards.siteDetails.targetedNumbers')}
        contentClassName="grid grid-flow-row text-left"
      >
        <DataTable
          className="size-full"
          noDataComponent={t('wards.common.noResults')}
          columns={productDataColumns(t, navigate, setSelectedProduct)}
          data={products}
          customStyles={productTableStyle}
          defaultSortFieldId="productNumber"
          defaultSortAsc={true}
          dense
        />
        <div className="flex justify-center space-x-4">
          <Button
            title={t('wards.siteDetails.bulkChange')}
            className={commonButtonClassName}
            onClick={() => {
              wardsNavigate(navigate, WaScreensMap[WA_Screen.StatusBulkChange].path);
            }}
          />
          <Button
            title={t('wards.common.back')}
            onClick={() => {
              navigate(-1);
            }}
            className={commonButtonClassName}
          />
        </div>
      </P_CollapsibleSection>
      <P_CollapsibleSection
        canToggle={false}
        title={t('wards.siteDetails.relatedSeNumber')}
        contentClassName="px-4 space-y-4"
      >
        <DataTable
          className="size-full"
          noDataComponent={t('wards.common.noResults')}
          columns={productRelatedColumns(t, navigate, setSelectedProduct, products)}
          data={productMasterInfo}
          customStyles={productTableStyle}
          defaultSortFieldId="productNumber"
          defaultSortAsc={true}
          dense
        />
      </P_CollapsibleSection>
    </div>
  );
};

export default P4_2_SiteDetails;
